<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import CatalogProductSetsAPI from 'dashboard/api/catalogProductSets';
import CatalogSourcesAPI from 'dashboard/api/catalogSources';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';
import CatalogProductPickerTable from '../components/CatalogProductPickerTable.vue';
import CatalogProductSetFilterBuilder from '../components/CatalogProductSetFilterBuilder.vue';
import CatalogProductSetPreviewList from '../components/CatalogProductSetPreviewList.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { accountId } = useAccount();

const setId = computed(() => route.params.setId);
const isEdit = computed(() => !!setId.value);

const loading = ref(false);
const saving = ref(false);
const error = ref('');
const sources = ref([]);

const name = ref('');
const setType = ref('manual');
const selectedProductIds = ref([]);
const filters = ref({ logic: 'all', rules: [] });

const breadcrumbItems = computed(() => [
  { label: t('CATALOG.PRODUCT_SETS.TITLE') },
  { label: isEdit.value ? t('CATALOG.PRODUCT_SETS.EDIT') : t('CATALOG.PRODUCT_SETS.NEW') },
]);

const fetchSources = async () => {
  try {
    const { data } = await CatalogSourcesAPI.list();
    sources.value = data.sources || [];
  } catch {
    sources.value = [];
  }
};

const loadSet = async () => {
  if (!isEdit.value) return;
  try {
    loading.value = true;
    const { data } = await CatalogProductSetsAPI.show(setId.value);
    const set = data.product_set;
    name.value = set.name;
    setType.value = set.set_type;
    filters.value = set.filters || { logic: 'all', rules: [] };
    if (set.set_type === 'manual') {
      selectedProductIds.value = data.selected_ids || [];
    }
  } catch (e) {
    error.value = e.response?.data?.error || e.message;
  } finally {
    loading.value = false;
  }
};

onMounted(() => Promise.all([fetchSources(), loadSet()]));

const goBack = () =>
  router.push({ name: 'catalog_product_sets', params: { accountId: accountId.value } });

const save = async () => {
  if (!name.value.trim()) {
    error.value = t('CATALOG.PRODUCT_SETS.FORM.NAME_REQUIRED');
    return;
  }
  try {
    saving.value = true;
    error.value = '';
    const payload = {
      name: name.value.trim(),
      set_type: setType.value,
      ...(setType.value === 'dynamic' ? { filters: filters.value } : {}),
    };
    const productIds = setType.value === 'manual' ? selectedProductIds.value : undefined;

    if (isEdit.value) {
      await CatalogProductSetsAPI.update(setId.value, payload, productIds);
    } else {
      await CatalogProductSetsAPI.create(payload, productIds);
    }
    goBack();
  } catch (e) {
    error.value = e.response?.data?.errors?.join(', ') || e.message;
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden bg-n-surface-1">
    <header class="shrink-0 px-6 lg:px-16">
      <div class="max-w-4xl mx-auto flex items-center justify-between py-7 gap-2">
        <Breadcrumb :items="breadcrumbItems" @click="goBack" />
        <Button size="sm" :is-loading="saving" solid blue @click="save">
          {{ t('CATALOG.PRODUCT_SETS.FORM.SAVE') }}
        </Button>
      </div>
    </header>

    <div class="flex-1 overflow-y-auto px-6 lg:px-16 scrollbar-custom">
      <div class="max-w-4xl mx-auto flex flex-col gap-8 pb-10">

        <div v-if="loading" class="flex items-center justify-center py-20">
          <Spinner />
        </div>

        <template v-else>
          <!-- Name -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CATALOG.PRODUCT_SETS.FORM.NAME_LABEL') }}
            </label>
            <Input
              v-model="name"
              :placeholder="t('CATALOG.PRODUCT_SETS.FORM.NAME_PLACEHOLDER')"
              class="max-w-md"
            />
          </div>

          <!-- Mode selector -->
          <div class="flex flex-col gap-3">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CATALOG.PRODUCT_SETS.FORM.MODE_LABEL') }}
            </label>
            <div class="flex gap-3">
              <button
                type="button"
                class="flex items-center gap-3 px-4 py-3 rounded-xl outline outline-1 -outline-offset-1 transition-colors text-sm"
                :class="setType === 'manual'
                  ? 'outline-n-brand bg-n-brand/5 text-n-slate-12'
                  : 'outline-n-container hover:bg-n-alpha-1 text-n-slate-11'"
                @click="setType = 'manual'"
              >
                <i class="i-lucide-hand text-lg" />
                <div class="text-left">
                  <p class="font-medium">{{ t('CATALOG.PRODUCT_SETS.FORM.MODE_MANUAL') }}</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">{{ t('CATALOG.PRODUCT_SETS.FORM.MODE_MANUAL_DESC') }}</p>
                </div>
              </button>
              <button
                type="button"
                class="flex items-center gap-3 px-4 py-3 rounded-xl outline outline-1 -outline-offset-1 transition-colors text-sm"
                :class="setType === 'dynamic'
                  ? 'outline-n-brand bg-n-brand/5 text-n-slate-12'
                  : 'outline-n-container hover:bg-n-alpha-1 text-n-slate-11'"
                @click="setType = 'dynamic'"
              >
                <i class="i-lucide-filter text-lg" />
                <div class="text-left">
                  <p class="font-medium">{{ t('CATALOG.PRODUCT_SETS.FORM.MODE_DYNAMIC') }}</p>
                  <p class="text-xs text-n-slate-10 mt-0.5">{{ t('CATALOG.PRODUCT_SETS.FORM.MODE_DYNAMIC_DESC') }}</p>
                </div>
              </button>
            </div>
          </div>

          <!-- Manual: product picker -->
          <div v-if="setType === 'manual'" class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('CATALOG.PRODUCT_SETS.FORM.SELECT_PRODUCTS') }}
            </label>
            <CatalogProductPickerTable v-model="selectedProductIds" />
          </div>

          <!-- Dynamic: filter builder + live preview -->
          <template v-if="setType === 'dynamic'">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ t('CATALOG.PRODUCT_SETS.FORM.FILTER_LABEL') }}
              </label>
              <div class="p-4 bg-n-solid-2 rounded-xl border border-n-weak">
                <CatalogProductSetFilterBuilder v-model="filters" :sources="sources" />
              </div>
            </div>

            <CatalogProductSetPreviewList :filters="filters" />
          </template>

          <div v-if="error" class="px-4 py-3 rounded-xl bg-n-ruby-3 text-sm text-n-ruby-12">
            {{ error }}
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
